import { useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import type { BrandingConfig } from '../client/api-types';
import type { ChatMessage } from './useChatState';
import { t } from '../lib/i18n';

interface MessageListProps {
  messages: ChatMessage[];
  branding: BrandingConfig;
  children?: ComponentChildren;
  agentTyping?: boolean;
  bubbleStyle?: 'rounded' | 'classic';
  /**
   * Called when the visitor taps a reply button attached to an
   * interactive agent message. The value is the button's `title`,
   * which we forward as a normal user_message so the flow's `menu` /
   * `wait_for_reply` node matches the visible text (WhatsApp
   * semantics). No-op if the widget is running in demo / preview mode.
   */
  onReplyButtonSelect?: (title: string) => void;
}

function DateSeparator() {
  return (
    <div class="flex justify-center py-2">
      <span class="text-[11px] font-semibold tracking-wider text-w-secondary bg-w-bb-bg px-3 py-1 rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
        {t('today')}
      </span>
    </div>
  );
}

/**
 * Determine tick status for user messages based on position and agent typing.
 * Last user msg before agent types → read. Others → delivered.
 */
function getTickStatus(
  msg: ChatMessage,
  index: number,
  messages: ChatMessage[],
  agentTyping: boolean,
): 'sent' | 'delivered' | 'read' | undefined {
  if (msg.role !== 'user') return undefined;

  // Find if there's an agent message after this one
  const hasAgentAfter = messages
    .slice(index + 1)
    .some((m) => m.role === 'agent');
  if (hasAgentAfter) return 'read';

  // Last user message and agent is typing → read
  if (agentTyping) return 'read';

  // Otherwise delivered
  return 'delivered';
}

export function MessageList({
  messages,
  branding,
  agentTyping = false,
  bubbleStyle = 'rounded',
  children,
  onReplyButtonSelect,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom using scrollTop (not scrollIntoView)
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, agentTyping]);

  return (
    <div
      ref={containerRef}
      class="flex-1 overflow-y-auto py-3 transition-[background] duration-[350ms]"
      style={{
        backgroundColor: 'var(--w-bg)',
        backgroundImage: 'var(--w-bg-pattern)',
        backgroundSize: '20px 20px',
      }}
      role="log"
      aria-live="polite"
      aria-label={t('messages_aria')}
    >
      {messages.length > 0 && <DateSeparator />}
      {messages.map((msg, i) => {
        // Grouping: is this the last message in a consecutive group from same sender?
        const next = messages[i + 1];
        const isLastInGroup = !next || next.role !== msg.role;

        // Reply buttons attached to this agent message only show while
        // the visitor hasn't answered yet. As soon as a user message
        // arrives after this one (whether via a button tap or free
        // text), hide the buttons — matches WhatsApp / QuickReplies UX
        // where the prompt does not persist after a response. When it
        // should not render we simply omit the callback; MessageBubble
        // treats an absent `onReplyButtonSelect` as "don't render."
        const hasReplyButtons =
          msg.role === 'agent' &&
          Array.isArray(msg.replyButtons) &&
          msg.replyButtons.length > 0;
        const userRepliedAfter =
          hasReplyButtons &&
          messages.slice(i + 1).some((m) => m.role === 'user');
        const bubbleReplyHandler =
          hasReplyButtons && !userRepliedAfter
            ? onReplyButtonSelect
            : undefined;

        return (
          <MessageBubble
            key={msg.id}
            message={msg}
            brandingColor={branding.color}
            bubbleStyle={bubbleStyle}
            isLastInGroup={isLastInGroup}
            tickStatus={getTickStatus(msg, i, messages, agentTyping)}
            onReplyButtonSelect={bubbleReplyHandler}
          />
        );
      })}
      {children}
      {agentTyping && <TypingIndicator />}
    </div>
  );
}
